import React from 'react';
import { motion } from 'framer-motion';
import { EyeIcon, ZapIcon, ShieldIcon } from 'lucide-react';
export function SnakeOil() {
  return <section className="bg-[#FAFAF8] py-20 md:py-28" data-id="element-352">
      <div className="max-w-3xl mx-auto px-6" data-id="element-353">
        {/* Block A: Skepticism */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} data-id="element-354">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1A1A1A] tracking-tight mb-10 leading-tight" data-id="element-355">
            You've Seen Laser Caps Before. Here's Why You Dismissed Them — And
            Why This Is Different.
          </h2>

          <p className="text-lg text-[#4B5563] leading-relaxed mb-6" data-id="element-356">
            Your skepticism about laser caps is earned. Here's exactly why it
            exists:
          </p>

          <ul className="space-y-4 text-lg text-[#4B5563] leading-relaxed mb-10" data-id="element-357">
            <li className="flex items-start gap-3" data-id="element-358">
              <span className="w-1.5 h-1.5 rounded-full bg-[#C8A04A] mt-3 flex-shrink-0" data-id="element-359" />
              <span data-id="element-360">
                Brands hide diode output specs behind vague "proprietary"
                language
              </span>
            </li>
            <li className="flex items-start gap-3" data-id="element-361">
              <span className="w-1.5 h-1.5 rounded-full bg-[#C8A04A] mt-3 flex-shrink-0" data-id="element-362" />
              <span data-id="element-363">
                LED vs. laser diode confusion is rampant — the difference in
                therapeutic output is real
              </span>
            </li>
            <li className="flex items-start gap-3" data-id="element-364">
              <span className="w-1.5 h-1.5 rounded-full bg-[#C8A04A] mt-3 flex-shrink-0" data-id="element-365" />
              <span data-id="element-366">
                Miracle timeline promises that make any rational person's scam
                radar fire
              </span>
            </li>
            <li className="flex items-start gap-3" data-id="element-367">
              <span className="w-1.5 h-1.5 rounded-full bg-[#C8A04A] mt-3 flex-shrink-0" data-id="element-368" />
              <span data-id="element-369">Cheap build quality at premium prices</span>
            </li>
            <li className="flex items-start gap-3" data-id="element-370">
              <span className="w-1.5 h-1.5 rounded-full bg-[#C8A04A] mt-3 flex-shrink-0" data-id="element-371" />
              <span data-id="element-372">
                No explanation of what wavelength is used or why it matters
              </span>
            </li>
          </ul>

          {/* Reddit-style callout */}
          <div className="bg-[#F0EDE6] border border-gray-200 rounded-lg p-8 mb-12" data-id="element-373">
            <p className="text-lg italic text-[#1A1A1A] mb-3" data-id="element-374">
              "My brother in Christ, it's an LED. Not a nuclear reactor."
            </p>
            <p className="text-[#4B5563]" data-id="element-375">
              We've read that thread. We built CrownControl™ specifically to
              answer it.
            </p>
          </div>
        </motion.div>

        {/* Block B: Specs */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.6
      }} data-id="element-376">
          <h3 className="text-2xl md:text-3xl font-bold text-[#1A1A1A] tracking-tight mb-8" data-id="element-377">
            What the Specs Actually Mean
          </h3>
        </motion.div>
      </div>

      {/* Spec Cards — wider */}
      <div className="max-w-6xl mx-auto px-6" data-id="element-378">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16" data-id="element-379">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.5,
          delay: 0
        }} className="bg-white border border-gray-200 rounded-lg p-8" data-id="element-380">
            <div className="w-12 h-12 rounded-full bg-[#FDF6E3] flex items-center justify-center mb-5" data-id="element-381">
              <EyeIcon className="w-6 h-6 text-[#C8A04A]" data-id="element-382" />
            </div>
            <h4 className="text-lg font-bold text-[#1A1A1A] mb-3" data-id="element-383">
              Wavelength: 650–670nm
            </h4>
            <p className="text-[#4B5563] leading-relaxed" data-id="element-384">
              This is the validated range for follicle cell penetration in the
              photobiomodulation literature. Peer-reviewed studies and FDA
              clearance submissions consistently cite this window. Devices
              outside this range are not delivering the same therapy —
              regardless of diode count.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="bg-white border border-gray-200 rounded-lg p-8" data-id="element-385">
            <div className="w-12 h-12 rounded-full bg-[#FDF6E3] flex items-center justify-center mb-5" data-id="element-386">
              <ZapIcon className="w-6 h-6 text-[#C8A04A]" data-id="element-387" />
            </div>
            <h4 className="text-lg font-bold text-[#1A1A1A] mb-3" data-id="element-388">
              Power Density: Calibrated to Clinical Specifications
            </h4>
            <p className="text-[#4B5563] leading-relaxed" data-id="element-389">
              The energy delivered per unit of scalp surface (irradiance)
              determines whether therapy reaches the follicle level.
              CrownControl™ is calibrated to the output specifications used in
              the trials that informed FDA 510(k) clearance in this device
              category.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="bg-white border border-gray-200 rounded-lg p-8" data-id="element-390">
            <div className="w-12 h-12 rounded-full bg-[#FDF6E3] flex items-center justify-center mb-5" data-id="element-391">
              <ShieldIcon className="w-6 h-6 text-[#C8A04A]" data-id="element-392" />
            </div>
            <h4 className="text-lg font-bold text-[#1A1A1A] mb-3" data-id="element-393">
              FDA 510(k) Clearance — What It Actually Means
            </h4>
            <p className="text-[#4B5563] leading-relaxed" data-id="element-394">
              At-home hair-loss devices that carry FDA 510(k) clearance have
              gone through a substantive regulatory review — not a rubber stamp.
              Manufacturers submit clinical evidence for a specific indication:
              hair loss stage, patient age range, skin phototype. CrownControl™
              is built in this cleared device class. Clearance documentation is
              available on request.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Block C: Expectations */}
      <div className="max-w-3xl mx-auto px-6" data-id="element-395">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.6
      }} data-id="element-396">
          <h3 className="text-2xl md:text-3xl font-bold text-[#1A1A1A] tracking-tight mb-8" data-id="element-397">
            Honest Expectations — Because You've Been Burned Before
          </h3>

          <p className="text-lg text-[#4B5563] leading-relaxed mb-6" data-id="element-398">
            Most brands won't say this. We will: this takes time. Hair biology
            operates on a months-long cycle.
          </p>

          <div className="space-y-6 mb-8" data-id="element-399">
            <div className="flex items-start gap-4" data-id="element-400">
              <span className="text-sm font-bold text-[#C8A04A] bg-[#FDF6E3] px-3 py-1 rounded flex-shrink-0 mt-0.5" data-id="element-401">
                Wk 2–4
              </span>
              <p className="text-[#4B5563] leading-relaxed" data-id="element-402">
                Many users notice reduced scalp itchiness — an early sign the
                anti-inflammatory effect is active.
              </p>
            </div>
            <div className="flex items-start gap-4" data-id="element-403">
              <span className="text-sm font-bold text-[#C8A04A] bg-[#FDF6E3] px-3 py-1 rounded flex-shrink-0 mt-0.5" data-id="element-404">
                Mo 3–4
              </span>
              <p className="text-[#4B5563] leading-relaxed" data-id="element-405">
                Reduced daily shedding and early follicle stabilization for
                consistent users.
              </p>
            </div>
            <div className="flex items-start gap-4" data-id="element-406">
              <span className="text-sm font-bold text-[#C8A04A] bg-[#FDF6E3] px-3 py-1 rounded flex-shrink-0 mt-0.5" data-id="element-407">
                Mo 4–6
              </span>
              <p className="text-[#4B5563] leading-relaxed" data-id="element-408">
                The window where hair density improvements can become measurable
                in some men who have used the protocol consistently.
              </p>
            </div>
          </div>

          <p className="text-lg text-[#4B5563] leading-relaxed" data-id="element-409">
            We are not promising full restoration. We are promising a documented
            mechanism, a calibrated device, and an honest conversation about
            what dermatology research suggests is possible.
          </p>
        </motion.div>
      </div>
    </section>;
}